import { useState, useEffect } from "react";
import Modal from "react-modal";
import { HelpCircle } from "lucide-react";
import api from '../../service/api';
import { proximaEtapa, textoProximaEtapa } from '../../data/fluxoAvaliacao';
import "../../css/style.css";
import "../../css/CapacidadeFuncionalQuestionario.css";

const PERGUNTAS_FUNCIONAL = [
    { id: 0, texto: "1. Tomar conta das necessidades pessoais (vestir-se sozinho)", tipo: "radio" },
    { id: 1, texto: "2. Tomar banho", tipo: "radio" },
    { id: 2, texto: "3. Caminhar fora de casa (1–2 quarteirões)", tipo: "radio" },
    { id: 3, texto: "4. Atividades domésticas leves (cozinhar, tirar pó, lavar pratos, varrer)", tipo: "radio" },
    { id: 4, texto: "5. Subir ou descer escadas", tipo: "radio" },
    { id: 5, texto: "6. Fazer compras no supermercado ou shopping", tipo: "radio" },
    { id: 6, texto: "7. Levantar e carregar 5 kg (pacote de arroz)", tipo: "radio" },
    { id: 7, texto: "8. Caminhar 6–7 quarteirões", tipo: "radio" },
    { id: 8, texto: "9. Caminhar 12–14 quarteirões", tipo: "radio" },
    { id: 9, texto: "10. Levantar e carregar 13 kg (mala média ou grande)", tipo: "radio" },
    { id: 10, texto: "11. Atividades domésticas pesadas (aspirar, esfregar pisos)", tipo: "radio" },
    { id: 11, texto: "12. Atividades vigorosas (grandes distâncias, mover objetos pesados, dançar)", tipo: "radio" },
];

const OPCOES_FUNCIONAL = [
    { id: 0, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 1, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 2, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 3, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 4, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 5, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 6, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 7, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 8, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 9, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 10, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
    { id: 11, opcoes: ["Realiza sem dificuldade", "Realiza com dificuldade", "Não realiza"] },
];

// Mapeamento para os cálculos matemáticos originais
const MAPA_PONTOS = {
    "Realiza sem dificuldade": 1,
    "Realiza com dificuldade": 0,
    "Não realiza": -1
};

function CapacidadeFuncionalQuestionario({ setActiveTab, aluno, evaluationId, setEvaluationId, evaluationData, updateEvaluationData }) {
    const [etapa, setEtapa] = useState(1);
    const [isNextModal, setIsNextModal] = useState(false);
    const [showInfo, setShowInfo] = useState(false);

    const proxima = proximaEtapa(aluno?.profile, 'CapFuncionalQuest');

    const [respostasSelecionadas, setRespostasSelecionadas] = useState(
        PERGUNTAS_FUNCIONAL.map(p => ({ id: p.id, valor: "" }))
    );

    useEffect(() => {
        if (evaluationData?.capacidadeFuncional?.respostas) {
            setRespostasSelecionadas(evaluationData.capacidadeFuncional.respostas);
        }
    }, [evaluationData]);

    const totalEtapas = 3;
    const porcentagemConclusao = (etapa / totalEtapas) * 100;

    // --- LÓGICA DE CÁLCULO (Mantendo sua lógica original) ---
    const getPontuacaoTotal = () => {
        return respostasSelecionadas.reduce((acc, r) => (MAPA_PONTOS[r.valor] === 1 ? acc + 1 : acc), 0);
    };

    const getIncapazesTotal = () => {
        return respostasSelecionadas.reduce((acc, r) => (MAPA_PONTOS[r.valor] === -1 ? acc + 1 : acc), 0);
    };

    const pontuacao = getPontuacaoTotal();
    const incapazes = getIncapazesTotal();

    const classificacao = () => {
        if (respostasSelecionadas.some(r => r.valor === "")) return "Selecione todas as alternativas";
        if (pontuacao <= 6 || incapazes >= 3) return "Baixo";
        if (pontuacao >= 7 && pontuacao <= 11) return "Moderado";
        if (pontuacao === 12) return "Avançado";
        return "";
    };

    const definirNivelPrescricao = () => {
        if (incapazes >= 6) return "NIVEL_I";
        if (incapazes >= 3) return "NIVEL_II";
        if (pontuacao <= 6) return "NIVEL_III";
        if (pontuacao <= 9) return "NIVEL_IV";
        if (pontuacao <= 11) return "NIVEL_V";
        return "NIVEL_VI";
    };

    // --- RECOMENDAÇÕES (Suas informações originais) ---
    const recomendacoesPorNivel = {
        NIVEL_I: { titulo: "NÍVEL I – Incapacidade Física", objetivos: ["Manutenção da mobilidade residual", "Prevenção de atrofia, rigidez e complicações secundárias", "Estímulo sensorial e neuromotor"], modalidade: ["Exercícios passivos ou ativo-assistidos", "Mobilizações articulares", "Exercícios respiratórios", "Posicionamentos funcionais"], frequencia: "3–5x/semana (sessões curtas)", intensidade: "Muito leve (0–1/10)", volume: "10–20 minutos por sessão", observacoes: ["Atuação integrada com fisioterapia", "Objetivo é preservação funcional, não performance"] },
        NIVEL_II: { titulo: "NÍVEL II – Fisicamente Dependente", objetivos: ["Recuperar autonomia parcial", "Melhorar força mínima e controle postural", "Reduzir risco de quedas"], modalidade: ["Exercícios ativo-assistidos e ativos", "Treino funcional básico (sentar–levantar assistido)", "Exercícios em cadeira", "Equilíbrio estático com apoio"], frequencia: "2–4x/semana", intensidade: "Leve (2–3/10 | 30–40% 1RM)", volume: "1–2 séries | 8–12 repetições" },
        NIVEL_III: { titulo: "NÍVEL III – Fisicamente Frágil", objetivos: ["Aumentar força global", "Melhorar equilíbrio dinâmico", "Aumentar tolerância ao esforço"], modalidade: ["Treinamento funcional estruturado", "Musculação com máquinas/guias", "Caminhada orientada", "Treino de equilíbrio e coordenação"], frequencia: "Força: 2–3x/semana | Aeróbio: 2x/semana", intensidade: "40–60% 1RM | 3–4/10", volume: "2–3 séries | 8–12 repetições | Aeróbio: 20–30 min" },
        NIVEL_IV: { titulo: "NÍVEL IV – Fisicamente Independente", objetivos: ["Aumentar reserva funcional", "Melhorar capacidade cardiorrespiratória", "Prevenir sarcopenia e quedas"], modalidade: ["Musculação completa", "Treinamento funcional avançado", "Aeróbio contínuo ou intervalado leve", "Agilidade e coordenação"], frequencia: "3–5x/semana", intensidade: "60–70% 1RM | Aeróbio 60–75% FCres", volume: "2–4 séries | 8–15 repetições | Aeróbio: 30–45 min" },
        NIVEL_V: { titulo: "NÍVEL V – Fisicamente Apto", objetivos: ["Performance funcional", "Manutenção de massa muscular e VO₂", "Prevenção de doenças crônicas"], modalidade: ["Musculação periodizada", "Treinos aeróbios variados", "Atividades esportivas recreacionais", "Treino neuromotor"], frequencia: "4–6x/semana", intensidade: "70–80% 1RM | 6–7/10", volume: "3–5 séries | 6–12 repetições | Aeróbio: 40–60 min" },
        NIVEL_VI: { titulo: "NÍVEL VI – Elite Física", objetivos: ["Performance esportiva", "Potência, força máxima e VO₂máx", "Especificidade da modalidade"], modalidade: ["Treinamento resistido avançado", "HIIT / Z4–Z5", "Pliometria criteriosa", "Treino específico do esporte"], frequencia: "5–6x/semana", intensidade: "80–95% 1RM | 7–9/10", volume: "Totalmente individualizado" },
    };

    const dadosNivel = recomendacoesPorNivel[definirNivelPrescricao()];

    // --- FUNÇÕES DE AÇÃO ---
    const atualizarResposta = (perguntaId, novoValor) => {
        setRespostasSelecionadas(prev => prev.map(item =>
            item.id === perguntaId ? { ...item, valor: novoValor } : item
        ));
    };

    const avancarEtapa = () => setEtapa(prev => prev + 1);
    const retrocederEtapa = () => setEtapa(prev => (prev > 1 ? prev - 1 : prev));

    const handleCapFuncRegistrar = async () => {
        const payload = {
            capacidadeFuncional: { respostas: respostasSelecionadas },
            evaluationId
        };

        try {
            const response = await api.post(`/api/student/${aluno.id}/evaluation/capacidade-funcional`, payload, { withCredentials: true });
            if (response.status === 200) {
                if (response.data.evaluationId && setEvaluationId) {
                    if (!evaluationId) {
                        if (!aluno.avaliacoesRealizadas) aluno.avaliacoesRealizadas = [];
                        aluno.avaliacoesRealizadas.push({
                            id: response.data.evaluationId,
                            data: new Date().toISOString()
                        });
                    }
                    setEvaluationId(response.data.evaluationId);
                }
                if (updateEvaluationData) {
                    updateEvaluationData({ capacidadeFuncional: payload.capacidadeFuncional });
                }
                setIsNextModal(true);
            }
        } catch (error) {
            console.error('Erro ao salvar Capacidade Funcional:', error);
        }
    };

    const renderPergunta = (pergunta) => {
        const rObj = respostasSelecionadas.find(item => item.id === pergunta.id);
        const valorAtual = rObj ? rObj.valor : "";
        const opcoesDaPergunta = OPCOES_FUNCIONAL.find(opt => opt.id === pergunta.id)?.opcoes || [];

        return (
            <div className="question-item" key={pergunta.id}>
                <div className="standard-title">{pergunta.texto}</div>
                <div className="answer-container">
                    {opcoesDaPergunta.map(opcao => (
                        <label className="answer-item" key={opcao}>
                            <input
                                type="radio"
                                className="answer-radio"
                                name={`funcional-${pergunta.id}`}
                                checked={valorAtual === opcao}
                                onChange={() => atualizarResposta(pergunta.id, opcao)}
                            />
                            <p className="answer-title">{opcao}</p>
                        </label>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="avaliacao-container">
            <div className="avaliacao-content">

                {/* ETAPA 1 e 2: Questionários */}
                {etapa === 1 && (
                    <div className="questions-container">
                        <div className="questions-left">
                            {PERGUNTAS_FUNCIONAL.slice(0, 4).map(renderPergunta)}
                        </div>
                        <div className="questions-right">
                            {PERGUNTAS_FUNCIONAL.slice(4, 8).map(renderPergunta)}
                        </div>
                    </div>
                )}

                {etapa === 2 && (
                    <div className="questions-container">
                        <div className="questions-left">
                            {PERGUNTAS_FUNCIONAL.slice(8, 12).map(renderPergunta)}
                        </div>
                    </div>
                )}

                {/* ETAPA 3: Resultados e Classificação */}
                {etapa === 3 && (
                    <div className="questions-container">
                        <div className="questions-left">
                            <div className="question-title">Resultado da Capacidade Funcional</div>
                            <div className="indice-cap-func-quest">
                                <strong>{classificacao()}</strong> <br />
                                Pontuação: {pontuacao} / 12 <br />
                                Itens não realizados: {incapazes}
                            </div>
                            <div className="interpretacao-cap-func-quest">
                                <p className={classificacao() === "Avançado" ? "risko-1" : ""}>Avançado: realiza 12 itens sem dificuldade</p>
                                <p className={classificacao() === "Moderado" ? "risko-3" : ""}>Moderado: realiza 7 a 11 itens sem dificuldade</p>
                                <p className={classificacao() === "Baixo" ? "risko-5" : ""}>Baixo: ≤ 6 itens sem dificuldade ou incapaz de realizar 3 ou mais itens</p>
                            </div>
                        </div>

                        <div className="questions-right">
                            <div className="question-title">Classificação Funcional</div>
                            <div className="classificacao-cap-func-quest">
                                <h3>{dadosNivel.titulo}</h3>
                                <p><strong>Objetivos:</strong></p>
                                <ul>{dadosNivel.objetivos.map((o, i) => <li key={i}>{o}</li>)}</ul>
                                <p><strong>Modalidade:</strong></p>
                                <ul>{dadosNivel.modalidade.map((m, i) => <li key={i}>{m}</li>)}</ul>
                                <p><strong>Frequência:</strong> {dadosNivel.frequencia}</p>
                                <p><strong>Intensidade:</strong> {dadosNivel.intensidade}</p>
                                <p><strong>Volume:</strong> {dadosNivel.volume}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* FOOTER PADRONIZADO */}
                <div className="pagination-footer">
                    <div className="sub-pagination-footer">
                        <div className="sub-pagination-buttons">
                            <button className="next-prev-button" type="button" onClick={retrocederEtapa}>Voltar</button>
                            {etapa < 3 ? (
                                <button className="next-prev-button" type="button" onClick={avancarEtapa}>Avançar</button>
                            ) : (
                                <button className="next-prev-button" type="button" onClick={handleCapFuncRegistrar}>Registrar</button>
                            )}
                        </div>
                        <div className="progress-container">
                            <div className="progress-bar" style={{ width: `${porcentagemConclusao}%` }}></div>
                        </div>
                    </div>

                    <div className="info-box-container" onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>
                        <div className="info-button">
                            <HelpCircle size={20} /> Mais informações
                        </div>
                        {showInfo && (
                            <div className="info-box-content">
                                <p>A avaliação da capacidade funcional para Atividades da Vida Diária (AVDs) em idosos analisa o nível de independência e autonomia na realização de tarefas essenciais do cotidiano, como caminhar, sentar, levantar, vestir-se, tomar banho e realizar deslocamentos com segurança.
                                    <br/><br/>
                                    Por meio de testes específicos, é possível identificar limitações físicas, riscos de quedas, redução de mobilidade e necessidades de intervenção, contribuindo para a prevenção de incapacidades e promoção da qualidade de vida.
                                    <br/><br/>
                                    Essa avaliação é fundamental para orientar programas de exercício físico mais seguros e individualizados, com foco na manutenção da funcionalidade, independência e saúde global do idoso.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                <Modal isOpen={isNextModal} onRequestClose={() => setIsNextModal(false)} contentLabel="Confirmacao" className="modal-next" overlayClassName="popup-overlay">
                    <p className="popupNext-h1">Classificação Funcional finalizada!</p>
                    <p className="popupNext-h2">{textoProximaEtapa(proxima)}</p>
                    <div className="sub-pagination-buttons">
                        <button className="next-prev-button" type="button" onClick={() => proxima && setActiveTab(proxima.chave)}>Sim</button>
                        <button className="next-prev-button" type="button" onClick={() => setIsNextModal(false)}>Não</button>
                    </div>
                </Modal>
            </div>
        </div>
    );
}

export default CapacidadeFuncionalQuestionario;